import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { supabase } from '../../services/supabase';

const HmrcSyncHistoryModal = ({ isOpen, onClose }) => {
    const [syncRuns, setSyncRuns] = useState([]);
    const [expandedRun, setExpandedRun] = useState(null);
    const [syncItems, setSyncItems] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const fetchSyncRuns = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('hmrc_sync_runs')
                .select('*')
                .order('started_at', { ascending: false })
                .limit(20);

            if (error) throw error;
            setSyncRuns(data || []);
        } catch (err) {
            console.error('Failed to fetch sync runs:', err);
            alert('Failed to load HMRC sync history.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSyncItems = async (runId) => {
        if (syncItems[runId]) {
            setExpandedRun(expandedRun === runId ? null : runId);
            return;
        }

        try {
            const { data, error } = await supabase
                .from('hmrc_sync_items')
                .select('*')
                .eq('run_id', runId)
                .order('synced_at', { ascending: false });

            if (error) throw error;
            setSyncItems(prev => ({ ...prev, [runId]: data || [] }));
            setExpandedRun(expandedRun === runId ? null : runId);
        } catch (err) {
            console.error('Failed to fetch sync items:', err);
            alert('Failed to load sync details.');
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchSyncRuns();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200 max-h-[85vh] flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <h3 className="text-lg font-bold text-slate-800">HMRC Sync History</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1 rounded-full">
                        <XCircle className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-3">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                            <span className="ml-2 text-slate-600">Loading sync history...</span>
                        </div>
                    ) : syncRuns.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">No sync runs found.</div>
                    ) : (
                        syncRuns.map(run => (
                            <div key={run.id} className="border border-slate-200 rounded-lg">
                                <button
                                    onClick={() => fetchSyncItems(run.id)}
                                    className="w-full px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors"
                                >
                                    <div className="flex-1 text-left">
                                        <div className="font-semibold text-slate-800">
                                            Run #{run.id}
                                            {run.trigger_source && <span className="text-xs text-slate-500 ml-2">({run.trigger_source})</span>}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Started: {new Date(run.started_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            {run.finished_at && ` • Finished: ${new Date(run.finished_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 ml-4">
                                        <div className="text-right text-xs">
                                            <div className="text-emerald-700 font-semibold">{run.synced_clients || 0} synced</div>
                                            <div className="text-red-700 font-semibold">{run.failed_clients || 0} failed</div>
                                            <div className="text-slate-500">{run.total_clients || 0} total</div>
                                        </div>
                                        {expandedRun === run.id ? (
                                            <ChevronUp className="w-5 h-5 text-slate-400" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-slate-400" />
                                        )}
                                    </div>
                                </button>

                                {expandedRun === run.id && syncItems[run.id] && (
                                    <div className="border-t border-slate-100 bg-slate-50 p-4 space-y-2 max-h-96 overflow-y-auto">
                                        {syncItems[run.id].map(item => (
                                            <div key={item.id} className="bg-white rounded p-3 text-sm border border-slate-200">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-slate-800">Client ID: {item.client_id}</div>
                                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold mt-1 ${
                                                            item.status === 'synced'
                                                                ? 'bg-emerald-100 text-emerald-700'
                                                                : 'bg-red-100 text-red-700'
                                                        }`}>
                                                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {new Date(item.synced_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                                {item.message && (
                                                    <div className="text-xs text-slate-600 mt-2">{item.message}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg">
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default HmrcSyncHistoryModal;
